import { useState } from "react";
import { useTdaStore } from "@/stores/tda.store";
import { InputField } from "@/components/common/input-field";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { tdaSchema } from "@/features/purchase-ticket/schema/tda.schmea";
import { toast } from "sonner";
import { VerifyTda } from "@/services/purchase.service";
import { cn } from "@/lib/utils";
import type {
  VerifyTdaData,
  VerifyTdaRequest,
} from "@/types/purchase-ticket/verifytda";

interface TDATicket {
  mdTicketTypeId: string;
  ticketTypeName: string;
  ticketCategoryName: string;
  requestedQuantity: number;
}

interface VerifyTdaLocationState {
  tdaTickets: TDATicket[];
  searchParams: string;
}

interface VerifiedTdaPassenger {
  mdTicketTypeId: string;
  passengerIdentityNo: string;
  firstName: string;
  lastName: string | null;
  identityType: string;
}

const FIELD_CLASS =
  "h-14 rounded-2xl border-border bg-card px-4 pr-14 text-sm font-medium text-muted-foreground placeholder:text-border";
const DUPLICATE_TDA_MESSAGE = "Duplicate MyKad/Passport number";

const normalizeIdentityNo = (value: string) =>
  value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

const splitTdaName = (name: string) => {
  const normalizedName = name.trim();
  const nameParts = normalizedName.match(/^(.*?)\s+(?:bin|binti)\s+(.+)$/i);

  return {
    firstName: nameParts?.[1].trim() || normalizedName,
    lastName: nameParts?.[2].trim() || null,
  };
};

export default function VerifyTdaPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [tdaValues, setTdaValues] = useState<Record<string, string>>({});
  const [duplicateTdaFieldKey, setDuplicateTdaFieldKey] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);

  const setVerifiedTdaPassengers = useTdaStore(
    (state) => state.setVerifiedTdaPassengers,
  );

  const state = location.state as VerifyTdaLocationState | null;

  const tdaTickets: TDATicket[] = state?.tdaTickets ?? [];
  const searchParams = state?.searchParams ?? "";

  const totalTdaPassengers = tdaTickets.reduce(
    (total: number, ticket: TDATicket) => total + ticket.requestedQuantity,
    0,
  );

  const tdaValueCounts = Object.values(tdaValues).reduce(
    (counts, value) => {
      const idNumber = normalizeIdentityNo(value.trim());

      if (!idNumber) {
        return counts;
      }

      counts[idNumber] = (counts[idNumber] ?? 0) + 1;

      return counts;
    },
    {} as Record<string, number>,
  );

  const hasDuplicateTdaValue = Object.values(tdaValueCounts).some(
    (count) => count > 1,
  );

  const isFormValid =
    totalTdaPassengers > 0 &&
    Object.keys(tdaValues).length === totalTdaPassengers &&
    !hasDuplicateTdaValue &&
    Object.values(tdaValues).every(
      (value) => tdaSchema.safeParse({ idNumber: value }).success,
    );

  const getTdaInputError = (fieldKey: string) => {
    const value = tdaValues[fieldKey]?.trim() ?? "";

    if (!value) {
      return undefined;
    }

    if (duplicateTdaFieldKey === fieldKey && (tdaValueCounts[value] ?? 0) > 1) {
      return DUPLICATE_TDA_MESSAGE;
    }

    const result = tdaSchema.safeParse({ idNumber: value });

    if (!result.success) {
      return result.error.issues[0]?.message;
    }

    return undefined;
  };

  const formatTdaInput = (value: string) => {
    if (!value) return "";

    // IC
    if (/^\d/.test(value)) {
      const digits = value.replace(/\D/g, "").slice(0, 12);

      if (digits.length <= 6) {
        return digits;
      }

      if (digits.length <= 8) {
        return `${digits.slice(0, 6)}-${digits.slice(6)}`;
      }

      return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
    }

    // Passport
    return value
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase()
      .slice(0, 15);
  };

  const handleSubmit = async () => {
    const result = Object.entries(tdaValues).every(
      ([, value]) => tdaSchema.safeParse({ idNumber: value }).success,
    );

    if (!result) {
      toast.error("Please enter valid MYKAD/Passport numbers.");
      return;
    }

    if (hasDuplicateTdaValue) {
      toast.error(DUPLICATE_TDA_MESSAGE);
      return;
    }

    setLoading(true);

    try {
      const payload: VerifyTdaRequest = {
        passengerIdentityNoList: Object.values(tdaValues).map((idNumber) => ({
          passengerIdentityNo: idNumber,
        })),
      };

      const verificationResponse = await VerifyTda(payload);
      const verifiedPassengersByIdentityNo = new Map(
        (Array.isArray(verificationResponse.data)
          ? verificationResponse.data
          : [verificationResponse.data]
        ).map((passenger: VerifyTdaData) => [
          normalizeIdentityNo(passenger.identityNo),
          passenger,
        ]),
      );

      // Build verified passenger list
      const verifiedTdaPassengers: VerifiedTdaPassenger[] = [];

      tdaTickets.forEach((ticket) => {
        for (let i = 0; i < ticket.requestedQuantity; i++) {
          const fieldKey = `${ticket.mdTicketTypeId}-${i}`;
          const passengerIdentityNo = tdaValues[fieldKey];

          if (!passengerIdentityNo) {
            continue;
          }

          const verifiedPassenger = verifiedPassengersByIdentityNo.get(
            normalizeIdentityNo(passengerIdentityNo),
          );

          if (!verifiedPassenger) {
            continue;
          }

          const { firstName, lastName } = splitTdaName(verifiedPassenger.name);

          verifiedTdaPassengers.push({
            mdTicketTypeId: ticket.mdTicketTypeId,
            passengerIdentityNo: verifiedPassenger.identityNo,
            firstName,
            lastName,
            identityType: verifiedPassenger.identityType,
          });
        }
      });

      // Save verification result to Zustand (Store)
      setVerifiedTdaPassengers(verifiedTdaPassengers);

      toast.success("TDA verification successful.");

      navigate(`/purchase-ticket/select-trip?${searchParams}`, {
        state: { isVerifyTda: true },
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to verify Identity No.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/purchase-ticket")}
            aria-label="Go back"
            className="flex cursor-pointer items-center gap-2 text-lg font-semibold text-secondary-foreground md:text-xl"
          >
            <ChevronLeft
              size={24}
              className="text-primary"
              aria-hidden="true"
            />
            Verify Local
          </button>
        </div>
      </div>

      <section className="mt-2 grid grid-cols-1 gap-4 rounded-2xl bg-card px-4 py-6 shadow-xl md:mt-4 md:p-6">
        <span className="text-sm font-medium leading-tight text-muted">
          {totalTdaPassengers} passengers require verification
        </span>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {tdaTickets.map((ticket) =>
            Array.from({ length: ticket.requestedQuantity }).map((_, index) => {
              const fieldKey = `${ticket.mdTicketTypeId}-${index}`;

              return (
                <InputField
                  key={fieldKey}
                  label={`${ticket.ticketTypeName} ${index + 1}`}
                  placeholder="Enter MYKAD/Passport"
                  className={cn(
                    FIELD_CLASS,
                    getTdaInputError(fieldKey) && "border-destructive",
                  )}
                  error={getTdaInputError(fieldKey)}
                  value={tdaValues[fieldKey] ?? ""}
                  onChange={(e) => {
                    const value = formatTdaInput(e.target.value);

                    setTdaValues((prev) => {
                      const duplicateField = Object.entries(prev).some(
                        ([key, existingValue]) =>
                          key !== fieldKey &&
                          value.trim() !== "" &&
                          existingValue.trim() === value.trim(),
                      );

                      setDuplicateTdaFieldKey(duplicateField ? fieldKey : null);

                      return {
                        ...prev,
                        [fieldKey]: value,
                      };
                    });
                  }}
                />
              );
            }),
          )}

          <div className="hidden justify-end md:col-span-3 md:flex">
            <Button
              type="button"
              variant="default"
              className="w-2xs cursor-pointer"
              onClick={handleSubmit}
              disabled={!isFormValid || loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submit...
                </>
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>
      </section>

      <div className="mx-auto flex w-full max-w-7xl items-center gap-4 px-4 py-4 sm:px-6 lg:px-8 md:hidden">
        <Button
          type="button"
          variant="default"
          className="w-full cursor-pointer"
          onClick={handleSubmit}
          disabled={!isFormValid || loading}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submit...
            </>
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </div>
  );
}
